import { put, call, takeEvery, all, SagaReturnType } from "redux-saga/effects";
import { PayloadAction } from "@reduxjs/toolkit";

import { SetAuthInfoData } from "types";
import {
  login,
  tokenLogin,
  refreshToken,
  restorePassword,
  resetPassword,
  confirmPhone,
  resendSms,
  confirmBirthdate,
} from "api/auth";
import {
  AUTH_TOKEN,
  REFRESH_TOKEN,
  clearLocalState,
  writeToLocalState,
} from "utils/localStorage";
import { MAX_ATTEMPTS_REACHED } from "constants/errors";
import {
  loginAction,
  tokenLoginAction,
  logoutAction,
  refreshTokenAction,
  recoverPasswordAction,
  confirmPhoneAction,
  resetPasswordAction,
  confirmBirthdateAction,
  setAuthInfo,
  resendSmsAction,
} from "./auth.reducer";

function* loginFlow(action: typeof loginAction.types.triggerAction) {
  const { payload } = action;
  try {
    const { data }: SagaReturnType<typeof login> = yield call(login, payload);
    yield call(loginAction.sagas.resolve, action, data);
  } catch (error: any) {
    let formErrors: { [key: string]: string } = {};
    if (error.status === 500) {
      formErrors["phone"] = "Сервис временно недоступен";
    } else {
      switch (error?.data?.message) {
        case "max_attempts_reached":
          formErrors["phone"] = MAX_ATTEMPTS_REACHED;
          break;
        default:
          formErrors["phone"] = "Неверные данные авторизации";
          formErrors["password"] = "Неверные данные авторизации";
      }
    }
    yield call(loginAction.sagas.reject, action, formErrors);
  }
}

function* tokenLoginFlow(action: typeof tokenLoginAction.types.triggerAction) {
  const { payload } = action;
  try {
    const { data }: SagaReturnType<typeof tokenLogin> = yield call(
      tokenLogin,
      payload
    );
    yield call(tokenLoginAction.sagas.resolve, action, data);
  } catch (error: any) {
    if (error.status === 500) {
      yield call(
        tokenLoginAction.sagas.reject,
        action,
        "Сервис временно недоступен"
      );
    } else {
      yield call(tokenLoginAction.sagas.reject, action, "Неверная ссылка");
    }
  }
}

function* refreshTokenFlow(
  action: typeof refreshTokenAction.types.triggerAction
) {
  const { payload } = action;
  try {
    const {
      data: { token, refresh_token },
    }: SagaReturnType<typeof refreshToken> = yield call(refreshToken, payload);
    yield put(setAuthInfo({ data: { token, refreshToken: refresh_token } }));
    yield call(refreshTokenAction.sagas.resolve, action, null);
  } catch (error: any) {
    yield call(refreshTokenAction.sagas.reject, action, error);
  }
}

function* confirmPhoneFlow(
  action: typeof confirmPhoneAction.types.triggerAction
) {
  try {
    const { data } = yield call(confirmPhone, action.payload);
    yield call(confirmPhoneAction.sagas.resolve, action, data);
  } catch (error: any) {
    yield call(confirmPhoneAction.sagas.reject, action, error);
  }
}

function* recoverPasswordFlow(
  action: typeof recoverPasswordAction.types.triggerAction
) {
  try {
    const { data } = yield call(restorePassword, action.payload);
    yield call(recoverPasswordAction.sagas.resolve, action, data);
  } catch (error: any) {
    yield call(recoverPasswordAction.sagas.reject, action, error);
  }
}

function* resendSmsFlow(action: typeof resendSmsAction.types.triggerAction) {
  try {
    const { data } = yield call(resendSms, action.payload);
    yield call(resendSmsAction.sagas.resolve, action, data);
  } catch (error: any) {
    yield call(resendSmsAction.sagas.reject, action, error);
  }
}

function* resetPasswordFlow(
  action: typeof resetPasswordAction.types.triggerAction
) {
  try {
    yield call(resetPassword, action.payload);
    yield call(resetPasswordAction.sagas.resolve, action, null);
  } catch (error: any) {
    yield call(resetPasswordAction.sagas.reject, action, error);
  }
}

function* confirmBirthdateFlow(
  action: typeof confirmBirthdateAction.types.triggerAction
) {
  try {
    yield call(confirmBirthdate, action.payload);
    yield call(confirmBirthdateAction.sagas.resolve, action, null);
  } catch (error: any) {
    yield call(confirmBirthdateAction.sagas.reject, action, error);
  }
}

function setAuthInfoFlow(action: PayloadAction<SetAuthInfoData>) {
  const { data, rememberMe = true } = action.payload;
  writeToLocalState(AUTH_TOKEN, data.token);
  if (rememberMe) {
    writeToLocalState(REFRESH_TOKEN, data.token);
  }
}

function logoutFlow() {
  clearLocalState(AUTH_TOKEN);
  clearLocalState(REFRESH_TOKEN);
}

export default function* authSaga() {
  yield all([
    takeEvery(loginAction, loginFlow),
    takeEvery(tokenLoginAction, tokenLoginFlow),
    takeEvery(logoutAction, logoutFlow),
    takeEvery(refreshTokenAction, refreshTokenFlow),
    takeEvery(recoverPasswordAction, recoverPasswordFlow),
    takeEvery(confirmPhoneAction, confirmPhoneFlow),
    takeEvery(resendSmsAction, resendSmsFlow),
    takeEvery(resetPasswordAction, resetPasswordFlow),
    takeEvery(confirmBirthdateAction, confirmBirthdateFlow),
    takeEvery(setAuthInfo, setAuthInfoFlow),
  ]);
}
